import { useId } from "react";
import LayoutGridIcon from "../icons/LayoutGridIcon";
import ServerIcon from "../icons/ServerIcon";
import UserIcon from "../icons/UserIcon";
import KeyIcon from "../icons/KeyIcon";
import PlusCircleIcon from "../icons/PlusCircleIcon";
import SlidersIcon from "../icons/SlidersIcon";
import HelpCircleIcon from "../icons/HelpCircleIcon";
import { activeNavItemId, visibleNavGroups, type NavIcon, type NavItemId } from "../lib/nav-config";
import type { Route } from "../lib/routing";
import styles from "./SideNav.module.css";

interface Props {
  route: Route;
  isAdmin: boolean;
  /** Shows the MCP Servers item (REACT_APP_MCP_SERVERS_ENABLED). */
  mcpServersEnabled?: boolean;
  /** Shows the My Connections item (REACT_APP_MY_CONNECTIONS_ENABLED). */
  myConnectionsEnabled?: boolean;
  onSelect: (id: NavItemId) => void;
}

const ICONS: Record<NavIcon, (props: React.SVGProps<SVGSVGElement>) => React.ReactElement> = {
  grid: LayoutGridIcon,
  server: ServerIcon,
  user: UserIcon,
  key: KeyIcon,
  plus: PlusCircleIcon,
  sliders: SlidersIcon,
  help: HelpCircleIcon,
};

/**
 * The portal's side nav (#939, Feature C). Groups and items come from lib/nav-config.
 * Below 1024px it collapses to an icon rail; labels stay in the accessibility tree
 * (visually hidden, not removed) so every button keeps its accessible name.
 */
export default function SideNav({ route, isAdmin, mcpServersEnabled = false, myConnectionsEnabled = false, onSelect }: Props) {
  const idPrefix = useId();
  const activeId = activeNavItemId(route);

  return (
    <nav aria-label="Main" className={styles.sideNav}>
      <div className={styles.inner}>
        {visibleNavGroups({ isAdmin, mcpServersEnabled, myConnectionsEnabled }).map((group, index) => {
          // By index, not label: aria-labelledby is a space-separated list of ids, so a label
          // with a space ("Platform tools") would name nothing.
          const labelId = `${idPrefix}-group-${index}`;
          return (
            <div key={group.label} className={styles.group}>
              <span id={labelId} className={styles.groupLabel}>
                {group.label}
              </span>
              <ul aria-labelledby={labelId} className={styles.list}>
                {group.items.map((item) => {
                  const Icon = ICONS[item.icon];
                  const active = item.id === activeId;
                  return (
                    <li key={item.id}>
                      <button
                        type="button"
                        onClick={() => onSelect(item.id)}
                        aria-current={active ? "page" : undefined}
                        title={item.label}
                        className={`${styles.item}${active ? ` ${styles.itemActive}` : ""}`}
                      >
                        <Icon className={styles.icon} />
                        <span className={styles.label}>{item.label}</span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            </div>
          );
        })}
      </div>
    </nav>
  );
}
